/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  AppAlert,
  getRecaptchaToken,
  IBrowserError,
  IErrorMessage,
  LaddaButton,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
  RecaptchaTerm,
  RecaptchaTokenAction,
  SignUpFormValidator,
  useCaptcha,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  FaroFeatures,
  useLogger,
} from "@advapacs/common";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IRootState } from "../../config/reducers";
import { useAppDispatch } from "../../config/store";
import {
  IOrganRequestLanguage,
  defaultOrganisationRequest,
} from "../../types/signup";
import { signUpOrganisation } from "./ducks/operations";
import { CurrentSignUpStep, SignUpAction } from "./ducks/slices";
import { SuccessRegister } from "./SuccessRegister";
import { BlockRouter } from "../../components/BlockRouter";
import { AuthenticationLayout } from "@src/components/AuthenticationLayout";
import { SignUpLogo } from "./SignUpLogo";
import { SignUpCredentialsForm } from "./FormSteps/SignUpCredentialsForm";
import { PreferencesForm } from "./FormSteps/PreferencesForm";
import { StepPath } from "./StepPath";
import { OrganisationInformationForm } from "./FormSteps/OrganisationInformationForm";
import { getLanguages } from "../settingAccount/ducks/opertations";
import { useNavigate } from "react-router-dom";
import { SelectDomainType } from "@src/components/SelectDomainType";
import { TermAndServiceModal } from "@src/components/TermsAndService/TermAndServiceModal";
import { TermAndServiceWarning } from "@src/components/TermsAndService/TermAndServiceWarning";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { prodModeLog, reCaptchaDelay } from "@advapacs/common/src/utils/utils";
import {
  GOOGLE_RECAPTCHA_ENTERPRISE,
  TURNSTILE_SITE_KEY,
} from "@src/config/constants";
import { OrganisationRegistrationRequest } from "@advapacs/clinician-api-protos/dist/Registration";
import { LogLevel } from "@grafana/faro-web-sdk";

export const SignupOrganisation: FC = () => {
  // ============================ STATES =====================================
  const { t } = useTranslation(["login", "form"]);
  const captchaType = useCaptcha();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accountRef = useRef(null);
  const personalInformationRef = useRef(null);
  const preferencesRef = useRef(null);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executeRecaptchaRef = useRef(executeRecaptcha);
  const { languages } = useSelector(
    (state: IRootState) => state.settingAccountReducer
  );
  const { loading, organisationResponse, errorMessage, eulaSignUpAgreement } =
    useSelector((state: IRootState) => state.signUpSlice);
  const [browserError, setBrowserError] = useState<IBrowserError | null>(null);
  const [stepCount, setStepCount] = useState(1);
  const [currentStep, setCurrentStep] = useState(CurrentSignUpStep.CREDENTIAL);
  const [dirty, setDirty] = useState(true);
  const [, setError] = useState({});
  const [model, setModel] = useState<IOrganRequestLanguage>(
    defaultOrganisationRequest
  );
  const [turnstileError, setTurnstileError] = useState("");

  const [, setRecaptchaValue] = useState<string | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const tempTurnstileAction = useRef({
    action: "",
    data: {},
    lang: "",
  });

  const validateTurnstile = () => {
    if (!turnstileRef?.current || !window.turnstile) {
      setCaptchaLoading(false);
      return false;
    }
    return true;
  };

  const validator = useMemo(() => {
    return SignUpFormValidator(t);
  }, [t]);
  // ============================ LOGGER ===========================
  const logger = useLogger("[SignUpOrganisation]");

  /**
   * Step 1
   */
  const handleAccountInformation = () => {
    const {
      // @ts-ignore
      current: { validate, getData },
    } = accountRef;
    const { accountValidator } = validate();
    if (!accountValidator?.allValid()) {
      setError({});
      setStepCount(1);
      accountValidator?.showMessages();
      return;
    }
    accountValidator.hideMessages();
    const { accountModel } = getData();
    // Re-init model
    setModel({
      ...model,
      email: accountModel?.email,
      password: accountModel?.password,
      confirmPassword: accountModel?.confirmPassword,
    });
    // Next step
    setStepCount(2);
    setCurrentStep(CurrentSignUpStep.PERSONAL_INFORMATION);
  };
  /**
   * Step 2
   * @returns
   */

  const handlePersonalInformation = () => {
    const {
      // @ts-ignore
      current: { validate, getData },
    } = personalInformationRef;
    const { personalInformationValidator } = validate();
    if (!personalInformationValidator?.allValid()) {
      setError({});
      setStepCount(2);
      personalInformationValidator?.showMessages();
      return;
    }
    personalInformationValidator.hideMessages();
    const { personalInformationModel } = getData();
    // Re-init model
    setModel({
      ...model,
      name: personalInformationModel?.name,
      username: personalInformationModel?.username,
      organisationId: personalInformationModel?.organisationId,
      organisationName: personalInformationModel?.organisationName,
    });
    // Next step
    setStepCount(3);
    setCurrentStep(CurrentSignUpStep.PREFERENCES);
  };

  const generateRecaptchaToken = async (
    action: RecaptchaTokenAction,
    retries = RECAPTCHA_MAX_RETRIES
  ): Promise<string> => {
    setBrowserError(null); // reset error message when starting generate token.
    setCaptchaLoading(true);
    const currentExecuteRecaptcha = executeRecaptchaRef.current; // alway fresh
    // Script not loaded yet — retry until ready, then give up
    if (!currentExecuteRecaptcha) {
      if (retries > 0) {
        console.warn("Re-trigger waiting gRecaptcha loaded.");
        await reCaptchaDelay(RECAPTCHA_SCRIPT_LOAD_RETRIES);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      logger({
        message: "reCAPTCHA script not loaded yet.",
        action: "generateRecaptchaToken",
        level: LogLevel.INFO,
        feature: FaroFeatures.RECAPTCHA,
      });
      return Promise.reject(new Error("reCAPTCHA script not loaded yet."));
    }
    try {
      return await getRecaptchaToken(
        action,
        GOOGLE_RECAPTCHA_ENTERPRISE,
        currentExecuteRecaptcha
      );
    } catch (error) {
      if (retries > 0) {
        const delayTime =
          RECAPTCHA_RETRY_DELAY_MS * (RECAPTCHA_MAX_RETRIES - retries + 1); // linear/exponential backoff
        console.warn(
          `reCAPTCHA failed. Retrying in ${
            delayTime / 1000
          }s... (${retries} retries left)`
        );
        await reCaptchaDelay(delayTime);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      return Promise.reject((error as IErrorMessage)?.message || error);
    }
  };

  /**
   * Step 3
   */
  const handlePreferencesInformation = async () => {
    const {
      // @ts-ignore
      current: { validate, getData },
    } = preferencesRef;
    const { preferencesValidator } = validate();
    if (!preferencesValidator?.allValid()) {
      setError({});
      preferencesValidator?.showMessages();
      return;
    }
    preferencesValidator.hideMessages();
    const { preferencesModel } = getData();
    setModel({
      ...model,
      language: preferencesModel?.lang,
      //tRegion: preferencesModel?.defaultRegion,
    });

    // validate recaptcha
    if (!validator.allValid()) {
      validator.showMessages();
      setError({});
      return;
    }
    setDirty(false);

    if (captchaType?.provider === "turnstile") {
      if (!validateTurnstile()) {
        setTurnstileError("Turnstile not loaded in client Side.");
        return;
      }
      setTurnstileError(""); // reset error.
      // turnstile flow.
      window.turnstile?.reset(turnstileRef.current!); // reset before start
      tempTurnstileAction.current.action = "signUpOrganisation"; //
      tempTurnstileAction.current.data = {
        ...model,
        language: preferencesModel?.lang,
        defaultRegion: preferencesModel?.defaultRegion,
      };
      tempTurnstileAction.current.lang = preferencesModel?.lang;
      // trigger invisible challenge
      setCaptchaLoading(true); // trigger loading state.
      window.turnstile?.execute(turnstileRef.current!, {
        action: RecaptchaTokenAction.CP_REGISTRATION,
        async: false, // prevent async retries
      });

      prodModeLog(
        "Trigger execute generate function from turnstile, (signUpOrganisation function)"
      );
    } else {
      try {
        // avoid exception when trying generate recaptcha without ff.
        const recaptchaToken = await generateRecaptchaToken(
          RecaptchaTokenAction.CP_REGISTRATION
        );

        if (!recaptchaToken) throw Error("Missing Recaptcha Token");
        dispatch(
          signUpOrganisation(
            {
              ...model,
              language: preferencesModel?.lang,
              defaultRegion: preferencesModel?.defaultRegion,
            },
            recaptchaToken,
            preferencesModel?.lang
          )
        );
      } catch (err) {
        const errMsg =
          typeof err === "string"
            ? err
            : (err as IErrorMessage)?.message || "Unknown error messsage";
        setBrowserError({ message: errMsg });
      }
    }
  };

  const handleSignUpOrganisation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // reset error message to avoid useEffect trigger.
    dispatch(SignUpAction.clearErrorMessage());
    if (currentStep === CurrentSignUpStep.CREDENTIAL) {
      handleAccountInformation();
      return;
    }

    if (currentStep === CurrentSignUpStep.PERSONAL_INFORMATION) {
      handlePersonalInformation();
      return;
    }

    if (currentStep === CurrentSignUpStep.PREFERENCES) {
      handlePreferencesInformation();
      return;
    }
  };

  // @ts-ignore
  const handleMouseDown = (e) => {
    if (e.keyCode === 13) {
      handleSignUpOrganisation(e); // enter event.
    }
  };

  const handleBack = (stepBack: CurrentSignUpStep) => {
    // reset error message to avoid useEffect trigger.
    dispatch(SignUpAction.clearErrorMessage());
    //
    if (stepBack === CurrentSignUpStep.TYPE) {
      navigate("/SignUp/Select");
      return;
    }
    setCurrentStep(stepBack);

    if (stepBack === CurrentSignUpStep.PERSONAL_INFORMATION) setStepCount(2);
    else setStepCount(1);
  };

  const handleErrorBackToStep = useCallback(() => {
    const code = errorMessage?.code;
    switch (code) {
      case "8201":
        // Organisation ID is already registered.
        setCurrentStep(CurrentSignUpStep.PERSONAL_INFORMATION);
        setStepCount(2);
        break;

      default:
        break;
    }
  }, [errorMessage]);

  const handleCallbackFunction = (turnstileToken: string) => {
    switch (tempTurnstileAction.current.action) {
      case "signUpOrganisation":
        dispatch(
          signUpOrganisation(
            tempTurnstileAction.current.data as OrganisationRegistrationRequest,
            turnstileToken,
            tempTurnstileAction.current.lang,
            true
          )
        );
        break;
      default:
        break;
    }
    prodModeLog("Finish function " + tempTurnstileAction.current.action);
    // reset after callback called.
    window.turnstile?.reset(turnstileRef.current!); // reset
    tempTurnstileAction.current.action = "";
    tempTurnstileAction.current.data = {};
    tempTurnstileAction.current.lang = "";

    setCaptchaLoading(false);
  };

  /**
   * Effects handler.
   */

  // RESET RECAPTCHA AND FORM STATE
  useEffect(() => {
    dispatch(getLanguages());
    document.addEventListener("keydown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleMouseDown);
      setRecaptchaValue(null);
      dispatch(SignUpAction.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorMessage || currentStep === CurrentSignUpStep.PREFERENCES) {
      // back to current step
      if (errorMessage) handleErrorBackToStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, currentStep]);

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  // recaptcha effect.
  useEffect(() => {
    if (captchaType?.provider === "turnstile") {
      /**
         * The Turnstile token is one-time use — it expires quickly (typically within 2 minutes).
          After sending it to your backend (or after any failed verification), you’ll usually need to refresh / reset the widget to generate a new token.
         */

      const interval = setInterval(() => {
        if (
          typeof window !== "undefined" &&
          window?.turnstile &&
          turnstileRef?.current
        ) {
          // eslint-disable-next-line no-console
          console.log("call interval !!!");
          clearInterval(interval);

          if (turnstileRef.current.hasChildNodes()) {
            // avoid call re-render multiple times
            console.warn(
              "Cloudflare Turnstile has already been rendered. Prevent !!!"
            );
            return;
          }

          window.turnstile?.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: "invisible",
            callback: (turnstileToken: string) => {
              if (tempTurnstileAction.current.action !== "") {
                prodModeLog(
                  "Callback token from turnstile, will trigger function " +
                    tempTurnstileAction.current.action
                );
                handleCallbackFunction(turnstileToken);
              } else {
                prodModeLog("No action with callback function !!!");
              }
            },
            "expired-callback": () => {
              setCaptchaLoading(false);
              window.turnstile?.reset(turnstileRef.current!);
            },
            "error-callback": (turnstileErrorCode) => {
              // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
              setCaptchaLoading(false);
              // eslint-disable-next-line no-console
              console.log({ turnstileErrorCode });
              // window.turnstile?.reset(turnstileRef.current!);

              setTurnstileError(
                `Cloudflare Turnstile get error with code: ${turnstileErrorCode}`
              );
            },
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaType]);

  return (
    <>
      <div ref={turnstileRef}></div>
      {organisationResponse?.id ? (
        <SuccessRegister email={model.email} />
      ) : (
        <>
          <BlockRouter blocked={dirty} />
          <AuthenticationLayout
            isPersonalForm={
              currentStep === CurrentSignUpStep.PERSONAL_INFORMATION
            }
          >
            {errorMessage && <AppAlert.ErrorAlert error={errorMessage} />}
            <SignUpLogo t={t} />
            <SelectDomainType
              isOrg
              onIndividualClick={() => {
                navigate("/Signup/Select");
              }}
              onOrgClick={() => {}}
            />
            <StepPath
              isIndividual={false}
              wrapperClass="mb-2"
              onBack={handleBack}
              currentStep={currentStep}
              count={stepCount}
            />
            <SignUpCredentialsForm
              wrapperClass={`${
                currentStep !== CurrentSignUpStep.CREDENTIAL
                  ? "visible-hidden"
                  : ""
              }`}
              disabled={currentStep !== CurrentSignUpStep.CREDENTIAL}
              ref={accountRef}
              t={t}
            />
            <OrganisationInformationForm
              t={t}
              disabled={currentStep !== CurrentSignUpStep.PERSONAL_INFORMATION}
              wrapperClass={`${
                currentStep !== CurrentSignUpStep.PERSONAL_INFORMATION
                  ? "visible-hidden"
                  : ""
              }`}
              ref={personalInformationRef}
            />
            <PreferencesForm
              disabled={currentStep !== CurrentSignUpStep.PREFERENCES}
              languages={languages}
              wrapperClass={`${
                currentStep !== CurrentSignUpStep.PREFERENCES
                  ? "visible-hidden"
                  : ""
              }`}
              ref={preferencesRef}
              t={t}
            />

            <div
              className={`${
                currentStep !== CurrentSignUpStep.PREFERENCES
                  ? "visible-hidden"
                  : ""
              }`}
            >
              <LaddaButton
                loading={loading || captchaLoading}
                disabled={!eulaSignUpAgreement}
                className="btn btn-primary w-100 mt-2"
                onClickHandler={handleSignUpOrganisation}
                btnName={t("login:signup")}
              />

              <TermAndServiceWarning t={t} />
            </div>
            {currentStep !== CurrentSignUpStep.PREFERENCES && (
              <button
                type="button"
                onClick={handleSignUpOrganisation}
                className="btn btn-primary w-100 mt-2"
              >
                {t("next")}
              </button>
            )}

            {currentStep === CurrentSignUpStep.PREFERENCES &&
              errorMessage &&
              ["RecaptchaRiskException", "RecaptchaInvalidException"].includes(
                errorMessage.type
              ) && (
                <>
                  <div className="form-group m-t-xs m-b-none">
                    <AppAlert.SimpleAlert
                      className="m-b-none"
                      variant="warning"
                      message={errorMessage.message}
                    />
                  </div>
                </>
              )}
            {currentStep === CurrentSignUpStep.PREFERENCES && browserError && (
              <>
                <div className="form-group m-t-xs m-b-none">
                  <AppAlert.SimpleAlert
                    className="m-b-none"
                    variant="warning"
                    message={browserError.message}
                  />
                </div>
              </>
            )}

            {currentStep === CurrentSignUpStep.PREFERENCES &&
              turnstileError && (
                <>
                  <div className="form-group m-t-xs m-b-none">
                    <AppAlert.SimpleAlert
                      className="m-b-none"
                      variant="warning"
                      message={turnstileError}
                    />
                  </div>
                </>
              )}
            <RecaptchaTerm className="m-t-xs" />
            <TermAndServiceModal
              t={t}
              closeAction={() => {
                dispatch(SignUpAction.showSignUpEulaAgreementInfo(false));
              }}
            />
          </AuthenticationLayout>
        </>
      )}
    </>
  );
};
